import { analyticsService } from '../index-business';

export interface TeacherEventData {
  ownerId?: string;
  teacherId?: string;
  teacherData?: any;
  salaryData?: any;
  [key: string]: any;
}

export class TeacherTracker {
  static trackCreateTeacherAccount(data: {
    ownerId: string;
    id: string;
    teacher: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      teacher_id: data.id,
      teacher_name: data.teacher?.name,
      teacher_email: data.teacher?.email,
      teacher_phone: data.teacher?.phone ? 'has_phone' : 'no_phone',
      teacher_roles: data.teacher?.roles || [],
      teacher_studios: data.teacher?.studios || [],
      studios_count: data.teacher?.studios?.length || 0,
      has_avatar: data.teacher?.avatar ? true : false,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'createTeacherAccount',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Teacher Account Created', eventData);
  }

  static trackAddTeacher(data: {
    ownerId: string;
    teacherData: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      teacher_name: data.teacherData?.name,
      teacher_email: data.teacherData?.email,
      teacher_phone: data.teacherData?.phone ? 'has_phone' : 'no_phone',
      teacher_roles: data.teacherData?.roles || [],
      teacher_studios: data.teacherData?.studios || [],
      studios_count: data.teacherData?.studios?.length || 0,
      has_avatar: data.teacherData?.avatar ? true : false,
      has_description: data.teacherData?.description ? true : false,
      has_specialization: data.teacherData?.specialization ? true : false,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'addTeacher',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Teacher Added', eventData);
  }

  static trackUpdateTeacher(data: {
    ownerId: string;
    id: string;
    teacherData: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const updatedFields = data.teacherData ? Object.keys(data.teacherData) : [];

    const eventData = {
      owner_id: data.ownerId,
      teacher_id: data.id,
      updated_fields: updatedFields,
      teacher_name: data.teacherData?.name,
      teacher_email: data.teacherData?.email,
      teacher_phone: data.teacherData?.phone ? 'has_phone' : 'no_phone',
      teacher_roles: data.teacherData?.roles || [],
      teacher_studios: data.teacherData?.studios || [],
      studios_count: data.teacherData?.studios?.length || 0,
      has_avatar: data.teacherData?.avatar ? true : false,
      has_description: data.teacherData?.description ? true : false,
      has_specialization: data.teacherData?.specialization ? true : false,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'updateTeacher',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Teacher Updated', eventData);
  }

  static trackUpdateTeacherSalary(data: {
    ownerId: string;
    id: string;
    salaryData: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      teacher_id: data.id,
      salary_type: data.salaryData?.type, // hourly, per_class, monthly, etc.
      salary_amount: data.salaryData?.amount,
      salary_currency: data.salaryData?.currency,
      has_bonus: data.salaryData?.bonus ? true : false,
      bonus_amount: data.salaryData?.bonus,
      effective_from: data.salaryData?.effectiveFrom,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'updateTeacherSalary',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Teacher Salary Updated', eventData);
  }

  static trackDeleteTeacher(data: {
    ownerId: string;
    id: string;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      teacher_id: data.id,
      delete_type: 'soft_delete',
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'deleteTeacher',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Teacher Deleted', eventData);
  }

  static trackUndeleteTeacher(data: {
    ownerId: string;
    id: string;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      teacher_id: data.id,
      action_type: 'restore_teacher',
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'undeleteTeacher',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Teacher Restored', eventData);
  }

  static trackTeacherApiCall(
    endpoint: string,
    data: TeacherEventData & {
      success: boolean;
      error?: string;
      duration?: number;
    }
  ) {
    const eventData = {
      ...data,
      event_type: 'api_call',
      api_endpoint: endpoint,
      timestamp: new Date().toISOString(),
    };

    analyticsService.track(`Teacher API: ${endpoint}`, eventData);
  }
}

export const trackTeacherSuccess = (endpoint: string, data: TeacherEventData, duration?: number) => {
  TeacherTracker.trackTeacherApiCall(endpoint, {
    ...data,
    success: true,
    duration,
  });
};

export const trackTeacherError = (endpoint: string, error: string, data: TeacherEventData, duration?: number) => {
  TeacherTracker.trackTeacherApiCall(endpoint, {
    ...data,
    success: false,
    error,
    duration,
  });
};

export default TeacherTracker;
